import { WIDGET_SHADOW_ROOT_ENABLED_DATA_ATTR } from "@/constants";
import { WISHLIST_WIDGET_NAMES } from "@/constants/widget-names";
import { ModalManager } from "@/modal-manager";
import {
    globalProductIdManagerToken,
    globalProductVariantsIdManagerToken,
} from "@/tokens";
import { AddToWishlistProductPageWidgetDefinition } from "@apps/wishlist/definitions";
import { ProductIdFromAttributeProvider } from "@lib/ProductIdManager/ProductIdFromAttributeProvider";
import { ProductIdFromStaticProvider } from "@lib/ProductIdManager/ProductIdFromStaticProvider";
import { ProductIdManager } from "@lib/ProductIdManager/ProductIdManager";
import { ProductVariantIdFromAttributeProvider } from "@lib/ProductVariantIdManager/ProductVariantIdFromAttributeProvider";
import { ProductVariantIdFromUrlProvider } from "@lib/ProductVariantIdManager/ProductVariantIdFromUrlProvider";
import { ProductVariantsIdManager } from "@lib/ProductVariantIdManager/ProductVariantIdManager";
import AnalyticsEventsCollectorInterface from "@modules/analytics_events_collector/AnalyticsEventsCollector/AnalyticsEventsCollectorInterface";
import { AnalyticsEventsFactory } from "@modules/analytics_events_collector/AnalyticsEventsCollector/AnalyticsEventsFactory";
import { DynamicImportManager } from "@modules/dynamic_import/DynamicImportManager";
import WidgetInjectedSdkEvent from "@modules/sdk/common_sdk/events/WidgetInjectedSdkEvent";
import { dispatchSdkEvent } from "@modules/sdk/helpers";
import AttributesObserver from "@ui-utils/AttributesObserver/AttributesObserver";
import MutationObserverInjector from "@ui-utils/Injector/MutationObserverInjector";
import {
    parseIntDataAttr,
    parseIntFromGidDataAttr,
    parseOptionalBooleanDataAttr,
    parseStringDataAttr,
} from "@ui-utils/Injector/utils";
import { container } from "tsyringe";

import { LoggerInterface } from "@interfaces/LoggerInterface";

const OLD_BUTTON_CLASS_DATA_ATTR = "gw-button_class";
const OLD_PRODUCT_ID_DATA_ATTR = "gw-product_id";

const BUTTON_CLASS_DATA_ATTR = "gw-button-class";
const PRODUCT_ID_DATA_ATTR = "gw-product-id";
const VARIANT_ID_DATA_ATTR = "gw-variant-id";

class AddToWishlistProductPageWidgetInjector {
    constructor(
        private readonly placeholderSelector: string,
        private readonly logger: LoggerInterface,
        private readonly modalManager: ModalManager,
        private readonly eventsCollector: AnalyticsEventsCollectorInterface,
        private readonly analyticsEventsFactory: AnalyticsEventsFactory,
        private readonly dynamicImportManager: DynamicImportManager,
        private readonly moi: MutationObserverInjector,
        private readonly attributesObserver: AttributesObserver
    ) {}

    start() {
        this.logger.debug("AddToWishlistProductPageWidgetInjector.start", {
            placeholderSelector: this.placeholderSelector,
        });

        const targets$ = this.moi.register({
            targetSelector: this.placeholderSelector,
        });

        targets$.subscribe((placeholder) => {
            this.logger.debug(
                "AddToWishlistProductPageWidgetInjector found placeholder",
                { placeholder }
            );

            let buttonClass = parseStringDataAttr(
                placeholder,
                BUTTON_CLASS_DATA_ATTR
            );

            if (buttonClass === null) {
                buttonClass = parseStringDataAttr(
                    placeholder,
                    OLD_BUTTON_CLASS_DATA_ATTR
                );

                if (buttonClass !== null) {
                    this.eventsCollector.pushEvent(
                        this.analyticsEventsFactory.createWidgetInjectedWithAttribute(
                            WISHLIST_WIDGET_NAMES.ADD_TO_WISHLIST_PRODUCT_PAGE_WIDGET,
                            OLD_BUTTON_CLASS_DATA_ATTR
                        )
                    );
                }
            }

            let productIdFromDataAttr =
                parseIntDataAttr(placeholder, PRODUCT_ID_DATA_ATTR) ||
                parseIntFromGidDataAttr(placeholder, PRODUCT_ID_DATA_ATTR);

            if (productIdFromDataAttr === null) {
                productIdFromDataAttr = parseIntDataAttr(
                    placeholder,
                    OLD_PRODUCT_ID_DATA_ATTR
                );

                if (productIdFromDataAttr !== null) {
                    this.eventsCollector.pushEvent(
                        this.analyticsEventsFactory.createWidgetInjectedWithAttribute(
                            WISHLIST_WIDGET_NAMES.ADD_TO_WISHLIST_PRODUCT_PAGE_WIDGET,
                            OLD_PRODUCT_ID_DATA_ATTR
                        )
                    );
                }
            }

            const productIdManager =
                productIdFromDataAttr === null
                    ? container.resolve(globalProductIdManagerToken)
                    : new ProductIdManager();

            if (productIdFromDataAttr !== null) {
                productIdManager.registerProvider(
                    new ProductIdFromStaticProvider(productIdFromDataAttr)
                );
                productIdManager.registerProvider(
                    new ProductIdFromAttributeProvider(
                        this.attributesObserver,
                        placeholder,
                        PRODUCT_ID_DATA_ATTR
                    )
                );
            }

            const productVariantIdFromDataAttr =
                parseIntDataAttr(placeholder, VARIANT_ID_DATA_ATTR) ||
                parseIntFromGidDataAttr(placeholder, VARIANT_ID_DATA_ATTR);

            const productVariantsIdManager =
                productVariantIdFromDataAttr === null
                    ? container.resolve(globalProductVariantsIdManagerToken)
                    : new ProductVariantsIdManager();

            if (productVariantIdFromDataAttr !== null) {
                productVariantsIdManager.registerProvider(
                    new ProductVariantIdFromAttributeProvider(
                        this.attributesObserver,
                        placeholder,
                        VARIANT_ID_DATA_ATTR
                    )
                );
                productVariantsIdManager.registerProvider(
                    new ProductVariantIdFromUrlProvider()
                );
            }

            void (async () => {
                const AddToWishlistProductPageWidget =
                    await this.dynamicImportManager.importDefault(
                        AddToWishlistProductPageWidgetDefinition,
                        true
                    );

                const widget = new AddToWishlistProductPageWidget(
                    {
                        target: placeholder,
                        buttonClass: buttonClass || "",
                        isWidgetShadowRootEnabled: parseOptionalBooleanDataAttr(
                            placeholder,
                            WIDGET_SHADOW_ROOT_ENABLED_DATA_ATTR
                        ),
                    },
                    this.logger,
                    this.modalManager,
                    productIdManager,
                    productVariantsIdManager
                );

                await widget.init();

                this.eventsCollector.pushEvent(
                    this.analyticsEventsFactory.createWidgetInjected(
                        WISHLIST_WIDGET_NAMES.ADD_TO_WISHLIST_PRODUCT_PAGE_WIDGET
                    )
                );

                dispatchSdkEvent(
                    new WidgetInjectedSdkEvent(
                        WISHLIST_WIDGET_NAMES.ADD_TO_WISHLIST_PRODUCT_PAGE_WIDGET
                    )
                );
            })();
        });
    }
}

export default AddToWishlistProductPageWidgetInjector;
